import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Trash2 } from 'lucide-react';

import { groupHistoryByDate } from './useHistory';
import { clearHistory, deleteReadUnitHistory, listHistoryRange } from '@/db/history';
import { subscribeAccountDataRestored } from '@/features/account/accountDataEvents';
import { requestAccountDataAutoSync } from '@/features/account/accountDataAutoSync';
import type { HistoryRow } from '@/db/schema';
import { getSource } from '@/sources/registry';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePaginatedFetcher } from '@/components/usePaginatedFetcher';
import type { Page } from '@/sources/types';

/**
 * Number of rows fetched per "Load more" click. Tuned to be small enough to
 * stay snappy on mobile (a History row mounts an `<img>`), large enough
 * that "Load more" doesn't feel grudging.
 */
const HISTORY_PAGE_SIZE = 50;

/**
 * Full reading-history page. Groups by date bucket (today / yesterday / this
 * week / this month / older) and exposes per-row remove + a "clear all"
 * button.
 *
 * Pagination is cursor-based against the `by-lastSeen` IDB index: each page
 * advances the cursor by `HISTORY_PAGE_SIZE`. Users with thousands of read
 * rows only mount the rows they're actually looking at.
 */
export function History() {
  const [resetTick, setResetTick] = useState(0);
  const [confirmingClear, setConfirmingClear] = useState(false);

  useEffect(
    () => subscribeAccountDataRestored(() => setResetTick((tick) => tick + 1)),
    [],
  );

  const fetchHistoryPage = useCallback(
    async (page: number): Promise<Page<HistoryRow>> => {
      const offset = (page - 1) * HISTORY_PAGE_SIZE;
      const { rows, hasMore } = await listHistoryRange(offset, HISTORY_PAGE_SIZE);
      return { items: rows, page, hasMore };
    },
    // resetTick is folded into resetKey below; bumping it remounts the
    // page-1 fetch, which is exactly the behavior we want on remove/clear.
    [],
  );

  const {
    items: rows,
    loading,
    error,
    hasMore,
    loadMore,
    loadedPages,
  } = usePaginatedFetcher<HistoryRow>({
    fetcher: fetchHistoryPage,
    resetKey: resetTick,
  });

  const groups = useMemo(() => groupHistoryByDate(rows), [rows]);

  const remove = useCallback(async (row: HistoryRow) => {
    await deleteReadUnitHistory(row.source, row.comicId, row.readUnitId);
    requestAccountDataAutoSync();
    setResetTick((t) => t + 1);
  }, []);

  const clear = useCallback(async () => {
    await clearHistory();
    requestAccountDataAutoSync();
    setResetTick((t) => t + 1);
  }, []);

  if (loading && loadedPages === 0) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  if (error !== null && loadedPages === 0) {
    return (
      <p className="text-sm text-destructive">
        Couldn’t read history: {error instanceof Error ? error.message : String(error)}
      </p>
    );
  }

  if (rows.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No history yet</CardTitle>
          <CardDescription>
            Open a gallery and start reading — the reader records progress automatically. Reads are
            backed up to your Bunko account when you&apos;re signed in.
          </CardDescription>
        </CardHeader>
        <CardContent />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-end">
        {confirmingClear ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              Clear all reading history?
            </span>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                void clear();
                setConfirmingClear(false);
              }}
            >
              Yes, clear
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmingClear(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => setConfirmingClear(true)}>
            <Trash2 className="mr-1 h-3.5 w-3.5" />
            Clear all
          </Button>
        )}
      </div>

      {groups.map((group) => (
        <section key={group.bucket} className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {group.label} · {group.rows.length}
          </h2>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {group.rows.map((row) => {
              const sourceName = getSource(row.source)?.name ?? row.source;
              const pct =
                row.totalPages > 0
                  ? Math.max(0, Math.min(100, Math.round((row.lastPage / row.totalPages) * 100)))
                  : 0;
              return (
                <li
                  key={row.key}
                  className="group relative flex gap-3 rounded-md border border-border/40 bg-card p-2 transition-colors hover:bg-accent/30"
                >
                  <Link
                    to={historyReadPath(row)}
                    className="flex flex-1 gap-3"
                  >
                    <div className="aspect-[3/4] w-16 shrink-0 overflow-hidden rounded bg-muted">
                      {row.coverUrl && (
                        <img
                          src={row.coverUrl}
                          alt=""
                          referrerPolicy="no-referrer"
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <h3 className="line-clamp-2 text-sm font-medium leading-tight">
                        {row.titleSnapshot}
                      </h3>
                      {row.readUnitTitle && (
                        <p className="line-clamp-1 text-xs text-muted-foreground">
                          {row.readGroupTitle ? `${row.readGroupTitle} · ` : ''}
                          {row.readUnitTitle}
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                        <Badge variant="secondary" className="text-[10px]">
                          {sourceName}
                        </Badge>
                        <span>
                          p{row.lastPage} / {row.totalPages} · {pct}%
                        </span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        {new Date(row.lastSeen).toLocaleString()}
                      </p>
                      <div className="mt-1 h-1 overflow-hidden rounded bg-muted">
                        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </Link>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1 h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={() => void remove(row)}
                    aria-label="Remove from history"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {hasMore && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={loadMore}
            disabled={loading}
            aria-label="Load more"
          >
            {loading ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Loading…
              </>
            ) : (
              `Load more (${HISTORY_PAGE_SIZE} at a time)`
            )}
          </Button>
        </div>
      )}

      {error !== null && loadedPages > 0 && (
        <p className="text-xs text-destructive">
          Couldn’t load more history: {error instanceof Error ? error.message : String(error)}
        </p>
      )}
    </div>
  );
}

function historyReadPath(row: HistoryRow): string {
  const params = new URLSearchParams();
  if (row.readUnitId) params.set('unit', row.readUnitId);
  const query = params.toString();
  return `/source/${row.source}/comic/${encodeURIComponent(row.comicId)}${query ? `?${query}` : ''}`;
}
